import { inject, Injectable } from '@angular/core';
import { FieldDefinitionModel } from '../_index';
import {
  ConnectedDataInformationModel,
  ConnectedFieldModel,
  DataObjectAddConfigModel,
  DataObjectSeedData,
  DataObjectSeedDataInventory,
  DataObjectSeedItem,
  NullsPosition,
  ObjectDefinitionModel,
} from '../models/object-definition.model';
import { BusinessLogicService } from '../../business-logic/business-logic.service';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ProxyService } from '../../proxy/proxy.service';

@Injectable({
  providedIn: 'root',
})
export class ObjectDefinitionHelpers {
  private _businessLogicService = inject(BusinessLogicService);
  private _proxyService = inject(ProxyService);

  getSeedData(
    parentSystemCode?: string,
    addConfig?: Partial<DataObjectAddConfigModel>
  ) {
    if (!parentSystemCode || !addConfig?.seedData) {
      return undefined;
    }

    return addConfig.seedData[parentSystemCode];
  }

  shouldApplySeedData(seedItem: DataObjectSeedItem, target?: any) {
    return seedItem.businessLogic
      ? this._businessLogicService
          .evaluateConditions(seedItem.businessLogic, target)
          .pipe(map((result) => result.hasResult && result.result === true))
      : of(true);
  }

  applySeedData(
    source: any,
    target: any,
    seedData: DataObjectSeedData
  ): Observable<any> {
    if (!source || !target || !seedData) {
      return of(target);
    }

    const checks = seedData.map((seed) =>
      this.shouldApplySeedData(seed, target).pipe(
        map((canApply) => ({ seed, canApply }))
      )
    );

    return forkJoin(checks).pipe(
      map((results) => {
        results.forEach(({ seed, canApply }) => {
          if (canApply) {
            target[seed.targetField] =
              source[seed.sourceField] ?? seed.defaultValue;
          }
        });
        return target;
      })
    );
  }

  getConnectedDataConfig(
    objectDefinition: ObjectDefinitionModel
  ): ConnectedDataInformationModel[] {
    return (
      objectDefinition?.connectedDataConfig?.connectedObjectInformation ?? []
    );
  }

  isImmutableAfterCreate(fieldDef: FieldDefinitionModel): boolean {
    const noDefault =
      fieldDef.defaultValue === null ||
      fieldDef.defaultValue === undefined ||
      fieldDef.defaultValue === '';

    return fieldDef.isReadOnly && fieldDef.isRequired && noDefault;
  }

  getConnectedFields(
    sourceFieldCode: string,
    connectedFields?: DataObjectSeedDataInventory
  ): ConnectedFieldModel[] {
    if (!sourceFieldCode || !connectedFields) {
      return [];
    }

    const targetFields: ConnectedFieldModel[] = [];

    Object.entries(connectedFields).forEach(([objectType, seedDataItems]) => {
      seedDataItems.forEach((item) => {
        if (item.sourceField === sourceFieldCode && item.targetField) {
          const objectDefinition =
            this._proxyService.getObjectDefinitionBySystemCode(objectType);

          if (!objectDefinition) {
            return;
          }

          const allFieldDefinitions =
            ObjectDefinitionModel.getAllFieldDefinitions(objectDefinition);

          targetFields.push({
            targetObjectType: objectType,
            sourceField: item.sourceField,
            targetField: item.targetField,
            objectDefinition: {
              ...objectDefinition,
              objectFieldDefinitions: allFieldDefinitions,
            },
            selected: item.enabledByDefault ?? true,
            businessLogic: item.businessLogic,
          });
        }
      });
    });

    return targetFields.filter(
      (field, index, self) =>
        index ===
        self.findIndex(
          (f) =>
            f.targetObjectType === field.targetObjectType &&
            f.targetField === field.targetField
        )
    );
  }

  compareWithNulls = (
    a: any,
    b: any,
    nulls: NullsPosition,
    order: 0 | 1
  ): number => {
    const aIsNull = a == null;
    const bIsNull = b == null;

    if (aIsNull && bIsNull) {
      return 0;
    }
    if (aIsNull) {
      return nulls === NullsPosition.First ? -1 : 1;
    }
    if (bIsNull) {
      return nulls === NullsPosition.First ? 1 : -1;
    }

    const aNum = Number(a);
    const bNum = Number(b);

    const cmp =
      Number.isFinite(aNum) && Number.isFinite(bNum)
        ? aNum === bNum
          ? 0
          : aNum > bNum
          ? 1
          : -1
        : String(a).localeCompare(String(b), undefined, {
            sensitivity: 'base',
          });

    return order === 1 ? -cmp : cmp;
  };

  applyClientSideSort = (items: any[], sortFields: any[]) => {
    if (!items?.length || !sortFields?.length) {
      return;
    }

    items.sort((a, b) => {
      for (const field of sortFields) {
        const aValue = a?.[field.code] ?? null;
        const bValue = b?.[field.code] ?? null;

        const order = field.order === 1 ? 1 : 0;
        const cmp = this.compareWithNulls(aValue, bValue, field.nulls, order);
        if (cmp !== 0) {
          return cmp;
        }
      }

      return String(a.__Id ?? a.id ?? '').localeCompare(
        String(b.__Id ?? b.id ?? '')
      );
    });
  };
}
